const UNCOLLAPSE_HYSTERESIS_PX = 16;

interface Wrapper {
    wrapper: HTMLElement;
    panel: HTMLElement;
    toggle: HTMLButtonElement;
}

function measureNeededWidth(panel: HTMLElement): number {
    const items = Array.from(panel.children) as HTMLElement[];
    if (items.length === 0) return 0;

    const itemsWidth = items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
    const gap = parseFloat(getComputedStyle(panel).columnGap) || 0;
    return itemsWidth + gap * (items.length - 1);
}

function measureRowFit(wrapper: HTMLElement, panel: HTMLElement): { needed: number; available: number } {
    const wasCollapsed = wrapper.classList.contains('is-collapsed');
    if (wasCollapsed) wrapper.classList.remove('is-collapsed');

    const needed = measureNeededWidth(panel);
    const available = panel.clientWidth;

    if (wasCollapsed) wrapper.classList.add('is-collapsed');
    return { needed, available };
}

function syncPanelHidden(wrapper: HTMLElement, panel: HTMLElement): void {
    const collapsed = wrapper.classList.contains('is-collapsed');
    const isOpen = wrapper.classList.contains('is-open');
    panel.setAttribute('aria-hidden', String(collapsed && !isOpen));
}

function close(wrapper: HTMLElement, toggle: HTMLButtonElement, panel: HTMLElement): void {
    wrapper.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    syncPanelHidden(wrapper, panel);
}

function check({ wrapper, panel, toggle }: Wrapper): void {
    const currentlyCollapsed = wrapper.classList.contains('is-collapsed');
    const { needed, available } = measureRowFit(wrapper, panel);
    const collapsed = currentlyCollapsed ? needed > available - UNCOLLAPSE_HYSTERESIS_PX : needed > available;

    wrapper.classList.toggle('is-collapsed', collapsed);
    if (!collapsed) {
        close(wrapper, toggle, panel);
    } else {
        syncPanelHidden(wrapper, panel);
    }
}

export function initClinicSubnav(): void {
    const wrappers: Wrapper[] = [];

    document.querySelectorAll<HTMLElement>('.tab-navigation-wrapper').forEach((wrapper) => {
        const panel = wrapper.querySelector<HTMLElement>('#tab-navigation-panel');
        const toggle = wrapper.querySelector<HTMLButtonElement>('.tab-navigation__toggle');
        if (!panel || !toggle) return;

        const entry: Wrapper = { wrapper, panel, toggle };
        wrappers.push(entry);

        toggle.addEventListener('click', () => {
            const isOpen = wrapper.classList.toggle('is-open');
            toggle.setAttribute('aria-expanded', String(isOpen));
            syncPanelHidden(wrapper, panel);
        });
    });

    if (wrappers.length === 0) return;

    wrappers.forEach(check);
    window.addEventListener('resize', () => wrappers.forEach(check), { passive: true });

    document.fonts?.ready.then(() => wrappers.forEach(check));

    document.addEventListener('click', (e) => {
        const target = e.target as Node;
        wrappers.forEach(({ wrapper, panel, toggle }) => {
            if (wrapper.classList.contains('is-open') && !wrapper.contains(target)) {
                close(wrapper, toggle, panel);
            }
        });
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        const openEntry = wrappers.find(({ wrapper }) => wrapper.classList.contains('is-open'));
        if (!openEntry) return;
        close(openEntry.wrapper, openEntry.toggle, openEntry.panel);
        openEntry.toggle.focus();
    });
}
